/**
 * @sazeo/sdk — Asset Management hooks
 *
 * TanStack Query wrappers for the asset-management addon. All calls route through
 * the Edge Function at `/addons/assets/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompanyAsset {
  id: string;
  tenant_id: string;
  name: string;
  category: 'laptop' | 'phone' | 'monitor' | 'vehicle' | 'furniture' | 'equipment' | 'other';
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  serial_no: string | null;
  purchase_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyAssetCreateInput {
  name: string;
  category?: CompanyAsset['category'];
  status?: CompanyAsset['status'];
  serial_no?: string | null;
  purchase_date?: string | null;
  notes?: string | null;
}

export interface CompanyAssetUpdateInput extends Partial<CompanyAssetCreateInput> {}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const assetKeys = {
  all: ['assets'] as const,
  list: (tenantId: string) => [...assetKeys.all, 'list', tenantId] as const,
  myList: (tenantId: string) => [...assetKeys.all, 'my-list', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (same apiFetch pattern as contracts.ts)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useCompanyAssets(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: assetKeys.list(tenantId),
    queryFn: () => apiFetch<CompanyAsset[]>(supabase, 'addons/assets/assets'),
    enabled: Boolean(tenantId),
  });
}

export function useMyAssets(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: assetKeys.myList(tenantId),
    queryFn: () => apiFetch<CompanyAsset[]>(supabase, 'addons/assets/assets/mine'),
    enabled: Boolean(tenantId),
  });
}

export function useCreateAsset(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: (input: CompanyAssetCreateInput) =>
      apiFetch<CompanyAsset>(supabase, 'addons/assets/assets', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

export function useUpdateAsset(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CompanyAssetUpdateInput }) =>
      apiFetch<CompanyAsset>(supabase, `addons/assets/assets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

export function useDeleteAsset(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  void tenantId;
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/assets/assets/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
    },
  });
}

export function useAssignAsset(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { asset_id: string; assigned_to: string; notes?: string }) =>
      apiFetch(supabase, `addons/assets/${input.asset_id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ assigned_to: input.assigned_to, notes: input.notes ?? null }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asset-assignments', tenantId] }),
  });
}

export function useReturnAsset(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { asset_id: string; notes?: string }) =>
      apiFetch(supabase, `addons/assets/${input.asset_id}/return`, {
        method: 'POST',
        body: JSON.stringify({ notes: input.notes ?? null }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetKeys.all });
      void qc.invalidateQueries({ queryKey: ['asset-assignments', tenantId] });
    },
  });
}
